/**
 * Focused hook for divisional chakra custom settings management
 * Handles custom start point settings (degrees, label), chart settings read/write, and event emission
 */

import { devError } from '../utils/devLog';
import { useState, useCallback } from 'react';
import { useMultipleCharts } from '../components/common/AstrologyChart/MultipleChartsContext';
import { globalEventStream } from '../components/common/AstrologyChart/universal_architecture/core/EventStream';
import {
  DivisionalChakraType,
  ChakraStartPoint,
  CustomStartPointSettings,
  getDivisionalChakraConfig
} from '../config/divisionalChakraConfig';

export interface ChakraCustomSettingsState {
  customSettings: CustomStartPointSettings;
  setCustomSettings: React.Dispatch<React.SetStateAction<CustomStartPointSettings>>;
  getCustomSettings: () => CustomStartPointSettings;
  updateCustomSettings: (
    newSettings: CustomStartPointSettings,
    showChakra: boolean,
    setStartPoint: React.Dispatch<React.SetStateAction<ChakraStartPoint>>
  ) => Promise<void>;
}

const getDefaultCustomSettings = (chakraType: DivisionalChakraType): CustomStartPointSettings => ({
  degrees: 0,
  label: chakraType === 'muhurta' ? 'MU' : chakraType.toUpperCase()
});

/**
 * Hook for managing chakra custom settings (degrees + label) for the active chart
 */
export const useChakraCustomSettings = (
  chakraType: DivisionalChakraType,
  isLoading: boolean,
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>,
  setError: React.Dispatch<React.SetStateAction<string | null>>
): ChakraCustomSettingsState => {
  const { activeChartId, getActiveChart, updateChart } = useMultipleCharts();
  const config = getDivisionalChakraConfig(chakraType);
  const startPointKey = `${chakraType}StartPoint` as const;
  const customSettingsKey = `${chakraType}CustomSettings` as const;

  const getCustomSettings = useCallback((): CustomStartPointSettings => {
    const defaults = getDefaultCustomSettings(chakraType);
    try {
      if (!activeChartId) return defaults;

      const activeChart = getActiveChart();
      if (!activeChart?.settings) return defaults;

      const custom = (activeChart.settings as any)[customSettingsKey];
      if (!custom || typeof custom !== 'object') return defaults;

      return {
        degrees: typeof custom.degrees === 'number' ? custom.degrees : defaults.degrees,
        label: typeof custom.label === 'string' ? custom.label : defaults.label
      };
    } catch {
      return defaults;
    }
  }, [activeChartId, getActiveChart, customSettingsKey, chakraType]);

  const [customSettings, setCustomSettings] = useState<CustomStartPointSettings>(() => {
    if (activeChartId) {
      return getCustomSettings();
    }
    return getDefaultCustomSettings(chakraType);
  });

  const getStartPointSetting = useCallback((): ChakraStartPoint => {
    try {
      if (!activeChartId) {
        return config.defaultStartPoint;
      }
      const activeChart = getActiveChart();
      if (!activeChart || !activeChart.settings) {
        return config.defaultStartPoint;
      }
      const sp = (activeChart.settings as any)[startPointKey];
      if (!sp || typeof sp !== 'string') {
        return config.defaultStartPoint;
      }
      return sp as ChakraStartPoint;
    } catch {
      return config.defaultStartPoint;
    }
  }, [activeChartId, getActiveChart, startPointKey, config.defaultStartPoint]);

  const updateCustomSettings = useCallback(async (
    newSettings: CustomStartPointSettings,
    showChakra: boolean,
    setStartPoint: React.Dispatch<React.SetStateAction<ChakraStartPoint>>
  ): Promise<void> => {
    if (!activeChartId || !updateChart) return;

    setIsLoading(true);
    setError(null);

    try {
      const activeChart = getActiveChart();
      if (!activeChart) throw new Error('Active chart unavailable');

      // Get current settings to check what changed
      const currentSettings = getCustomSettings();
      const currentStartPoint = getStartPointSetting();

      // Validate degrees
      const degrees = Math.max(0, Math.min(360, newSettings.degrees));
      const validSettings = { degrees, label: newSettings.label || 'L1' };

      // Check if only label changed (degrees stayed the same AND already on custom start point)
      const onlyLabelChanged = currentStartPoint === 'custom' &&
                               currentSettings.degrees === degrees &&
                               currentSettings.label !== validSettings.label;

      await updateChart(activeChartId, {
        settings: {
          ...activeChart.settings,
          [customSettingsKey]: validSettings,
          [startPointKey]: 'custom' // Auto-switch to custom
        }
      });

      setCustomSettings(validSettings);
      setStartPoint('custom');

      // Emit event
      if (showChakra) {
        if (onlyLabelChanged) {
          // Only label changed - emit lightweight label update event
          globalEventStream.emit({
            id: `${chakraType}-label-${activeChartId}-${Date.now()}`,
            type: `${chakraType}.chakra.label.update`,
            source: `divisional-chakra-settings-hook-${chakraType}`,
            timestamp: Date.now(),
            chartId: activeChartId,
            data: {
              chartId: activeChartId,
              label: validSettings.label,
              degrees: validSettings.degrees
            }
          });
        } else {
          // Degrees changed - full re-render needed
          globalEventStream.emit({
            id: `${chakraType}-custom-${activeChartId}-${Date.now()}`,
            type: `${chakraType}.chakra.startpoint.change`,
            source: `divisional-chakra-settings-hook-${chakraType}`,
            timestamp: Date.now(),
            chartId: activeChartId,
            data: {
              chartId: activeChartId,
              startPoint: 'custom',
              customSettings: validSettings,
              [`show${chakraType.toUpperCase()}Chakra`]: showChakra
            }
          });
        }
      }
    } catch (error) {
      setError(`Failed to update custom settings: ${error}`);
    } finally {
      setIsLoading(false);
    }
  }, [activeChartId, getActiveChart, updateChart, getCustomSettings, getStartPointSetting, customSettingsKey, startPointKey, chakraType, setIsLoading, setError]);

  return {
    customSettings,
    setCustomSettings,
    getCustomSettings,
    updateCustomSettings
  };
};
